import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import NavbarPage from "../../components/Navbar/Navbar_Page";
import Social from "../../components/Social/Social";
import Footer from "../../components/Footer/footer";

import "../Index10/Index10.css";
import "../Features/Features.css";

/* What the importer brings across. Keep in step with pb_docs/importing/README.md. */
const MOVES = [
  {
    icon: "pe-7s-note2",
    title: "Posts, with their history",
    desc: "Every post keeps its original date, author, status and description. Each one links back to where it came from.",
  },
  {
    icon: "pe-7s-like2",
    title: "Votes from real people",
    desc: "Voters are matched by email, so nobody has to vote again. When they sign in, their history is already there.",
  },
  {
    icon: "pe-7s-comment",
    title: "Comments and internal notes",
    desc: "Comments arrive in order, with admin replies marked official. Internal comments become admin-only notes.",
  },
  {
    icon: "pe-7s-photo",
    title: "Images, copied",
    desc: "Screenshots are copied into our storage, so they keep working after you leave Canny.",
  },
  {
    icon: "pe-7s-news-paper",
    title: "Changelog",
    desc: "Changelog entries keep their publish dates and stay linked to the posts they shipped.",
  },
  {
    icon: "pe-7s-lock",
    title: "Private stays private",
    desc: "A private board becomes a private backlog. Nothing is emailed to your users unless you choose to send one notice.",
  },
];

const STEPS = [
  {
    title: "Paste your Canny API key",
    desc: "We scan your boards and show what will move before anything is imported. We only ever read from Canny.",
  },
  {
    title: "Map boards and statuses",
    desc: "Send each board to a new or existing backlog, check the suggested status mapping, and choose what to include.",
  },
  {
    title: "Let it run",
    desc: "The import runs in the background. Run it again on switch-over day to pick up anything new; what's already here is skipped.",
  },
  {
    title: "Point your links here",
    desc: "Download an old-URL to new-URL map for redirects on your own domain, and optionally tell voters their votes came with them.",
  },
];

const SwitchFromCanny = () => {
  const [navItems] = useState([
    { id: 1, link: "/", navheading: "Home" },
    { id: 2, idnm: "what-moves", navheading: "What moves" },
    { id: 3, idnm: "how", navheading: "How it works" },
    { id: 4, link: "/features", navheading: "Features" },
  ]);

  useEffect(() => {
    document.title = "Switch from Canny — Public Backlog";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home-ten fp-page">
      <div className="ht-mesh-bg">
        <div className="ht-blob ht-blob-1" />
        <div className="ht-blob ht-blob-2" />
        <div className="ht-blob ht-blob-3" />
        <div className="ht-blob ht-blob-4" />
      </div>
      <div className="ht-noise" />

      <div className="ht-content">
        <NavbarPage navItems={navItems} navClass="" />

        <section className="fp-hero">
          <div className="fp-hero-inner">
            <div className="ht-hero-eyebrow">
              <span className="ht-hero-eyebrow-dot" />
              Switching
            </div>
            <h1 className="ht-hero-title">
              Bring your Canny board,{" "}
              <span className="ht-gradient-text">votes and all</span>
            </h1>
            <p className="fp-hero-desc">
              The importer moves your posts, votes, comments, images and
              changelog straight from Canny's API. Your voters keep their
              votes, your links keep working, and nobody starts over.
            </p>
            <div className="fp-hero-stats">
              <span className="fp-stat-chip"><strong>Unlimited</strong> tickets on Free</span>
              <span className="fp-stat-chip"><strong>Votes</strong> matched by email</span>
              <span className="fp-stat-chip"><strong>Re-runnable</strong> until switch-over</span>
              <span className="fp-stat-chip"><strong>CSV</strong> for other tools</span>
            </div>
            <div className="ht-hero-actions" style={{ justifyContent: "center" }}>
              <a href="https://app.publicbacklog.com/user/login" className="ht-btn-primary">
                Start free and import
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
              <a
                href="https://docs.publicbacklog.com/#/importing/"
                target="_blank"
                rel="noopener noreferrer"
                className="ht-btn-secondary"
              >
                Read the migration guide
              </a>
            </div>
          </div>
        </section>

        <section id="what-moves" className="fp-section">
          <div className="fp-section-inner">
            <div className="fp-copy">
              <div className="ht-section-label">
                <span style={{ width: 16, height: 2, background: "currentColor", display: "inline-block" }} />
                What moves
              </div>
              <h2 className="ht-section-title">
                Everything your users{" "}
                <span className="ht-gradient-text">already gave you</span>
              </h2>
              <p className="fp-desc">
                Years of requests and votes are the valuable part of a feedback
                board. The importer brings them across intact.
              </p>
              <div className="fp-points fp-points-2col">
                {MOVES.map((p) => (
                  <div key={p.title} className="fp-point">
                    <div className="fp-point-icon"><i className={p.icon} /></div>
                    <div>
                      <div className="fp-point-title">{p.title}</div>
                      <div className="fp-point-desc">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="how" className="fp-section fp-flip">
          <div className="fp-section-inner">
            <div className="fp-copy">
              <div className="ht-section-label">
                <span style={{ width: 16, height: 2, background: "currentColor", display: "inline-block" }} />
                How it works
              </div>
              <h2 className="ht-section-title">
                Four steps,{" "}
                <span className="ht-gradient-text">no spreadsheets</span>
              </h2>
              <p className="fp-desc">
                Import before you downgrade or cancel Canny: its API stops
                working once a company drops to its free tier.
              </p>
              <div className="fp-points">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="fp-point">
                    <div className="fp-point-icon"><strong>{i + 1}</strong></div>
                    <div>
                      <div className="fp-point-title">{s.title}</div>
                      <div className="fp-point-desc">{s.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="ht-cta">
          <div className="ht-cta-glow" />
          <div className="ht-cta-inner">
            <h2 className="ht-cta-title">
              Coming from somewhere else?{" "}
              <span className="ht-gradient-text">Use a CSV.</span>
            </h2>
            <p className="ht-cta-desc">
              Any tool that exports a spreadsheet works: match your columns,
              map your statuses, and import. Free plan, unlimited tickets, no
              credit card.
            </p>
            <div className="ht-cta-actions">
              <a href="https://app.publicbacklog.com/user/login" className="ht-btn-primary">
                Get started for free
                <span style={{ fontSize: "1.1em" }}>&rarr;</span>
              </a>
              <Link to="/features" className="ht-btn-secondary">
                See all features
              </Link>
            </div>
          </div>
        </section>

        <Social />
        <Footer />
      </div>
    </div>
  );
};

export default SwitchFromCanny;
